/**
 * preventGestures — global, app-lifetime guards that stop the user from
 * scaling or right-clicking the app on any device (touch or desktop).
 *
 * Why JS and not just the viewport meta / CSS:
 * - iOS Safari IGNORES `user-scalable=no` / `maximum-scale` (since iOS 10), so
 *   pinch-to-zoom still fires. Its non-standard `gesturestart/change/end`
 *   events are the only reliable way to block it.
 * - Other engines (Android, iPadOS trackpad) pinch-zoom via multi-touch
 *   `touchmove`, blocked here by preventing any 2+ finger move.
 * - Long-press on touch and right-click on desktop both raise `contextmenu`;
 *   preventing it kills the native callout/menu everywhere (canvas + UI).
 *
 * Installed once at bootstrap (see index.jsx) and never removed — the guards
 * apply for the whole session, so there is nothing to dispose.
 */
export function installGestureGuards() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  // Right-click / long-press callout — kill everywhere.
  document.addEventListener('contextmenu', (e) => e.preventDefault())

  // iOS Safari pinch-zoom (non-standard gesture events).
  document.addEventListener('gesturestart', (e) => e.preventDefault())
  document.addEventListener('gesturechange', (e) => e.preventDefault())
  document.addEventListener('gestureend', (e) => e.preventDefault())

  // Multi-touch pinch-zoom on engines that don't emit gesture events.
  // Must be non-passive so preventDefault actually blocks the zoom.
  document.addEventListener(
    'touchmove',
    (e) => {
      if (e.touches.length > 1) e.preventDefault()
    },
    { passive: false }
  )

  // Double-tap-to-zoom fallback: block the second tap of a rapid pair.
  let lastTouchEnd = 0
  document.addEventListener(
    'touchend',
    (e) => {
      const now = Date.now()
      if (now - lastTouchEnd <= 300) e.preventDefault()
      lastTouchEnd = now
    },
    { passive: false }
  )
}
